<template>
  <div class="status-value-mapping">
    <div class="section">
      <div v-for="row in writeRows" :key="row.status" class="sentence-row">
        <span class="sentence-text">When the task becomes</span>
        <BaseButton kind="auxiliary" type="tertiary" state="active">
          <template #icon>
            <TaskCheckbox
              :status="row.status"
              :is-interactive="false"
              size="sm"
              class="no-pointer"
            />
          </template>
        </BaseButton>
        <span class="sentence-text">, set to</span>
        <ButtonMenu
          placement="bottom-start"
          :is-hidden-on-click="true"
          :is-disabled="!options.length"
        >
          <template #btn>
            <div v-if="row.label !== null" class="pick-chip">
              <BaseSelectOption
                :name="row.label"
                :color="getColor(row.label)"
                :square="false"
              />
            </div>
            <div v-else class="pick-trigger">
              <span class="pick-placeholder">…</span>
            </div>
          </template>
          <template #content>
            <div class="write-options">
              <div
                v-for="option in options"
                :key="option.value"
                class="write-choice"
                :class="{
                  active: modelValue.writes[row.status] === option.value,
                }"
                @click="setWrite(row.status, option.value)"
              >
                <BaseSelectOption
                  :name="option.label"
                  :color="getColor(option.label)"
                  :square="false"
                  full-width
                />
              </div>
            </div>
          </template>
        </ButtonMenu>
      </div>
    </div>

    <div v-if="readRows.length" class="section">
      <div class="grid read-grid">
        <span class="grid-header">
          When <strong>{{ propertyName }}</strong> is…
        </span>
        <span class="grid-header right">…task will be</span>
        <template v-for="row in readRows" :key="row.value">
          <BaseSelectOption
            :name="row.label"
            :color="getColor(row.label)"
            :square="false"
          />
          <div class="status-picks">
            <BaseButton
              v-for="status in mappedStatuses"
              :key="status"
              kind="auxiliary"
              type="tertiary"
              :state="row.status === status ? 'active' : 'default'"
              @click="toggleRead(row.value, status)"
            >
              <template #icon>
                <TaskCheckbox
                  :status="status"
                  :is-interactive="false"
                  size="sm"
                  class="no-pointer"
                />
              </template>
            </BaseButton>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseSelectOption from "@/components/base/BaseSelectOption.vue";
import { getColor } from "@/components/color";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import {
  mappedStatuses,
  readStatusFor,
  setWriteValue,
  toggleReadStatus,
  unpinnedValues,
  type StatusValueMappingState,
  type StatusValueOption,
} from "@/components/settings/status-value-mapping";
import TaskCheckbox, {
  type TaskStatus,
} from "@/components/task/TaskCheckbox.vue";

/**
 * Sentence-style editor for a status mapping: one row per Routine status for
 * the values written back, then a grid for how the remaining values read in.
 * The column itself is picked by the parent, since a mapping only ever reacts
 * to a single column.
 */
export default defineComponent({
  name: "StatusValueMapping",
  components: { BaseButton, BaseSelectOption, ButtonMenu, TaskCheckbox },
  props: {
    modelValue: {
      type: Object as PropType<StatusValueMappingState>,
      required: true,
    },
    options: {
      type: Array as PropType<StatusValueOption[]>,
      required: true,
    },
    propertyName: { type: String, required: true },
  },
  emits: {
    "update:modelValue": (_: StatusValueMappingState) => true,
  },
  setup(props, { emit }) {
    const labelFor = (value: string): string =>
      props.options.find((option) => option.value === value)?.label ?? value;

    const writeRows = computed(() =>
      mappedStatuses.map((status) => {
        const value = props.modelValue.writes[status];
        return {
          status,
          label: value === undefined ? null : labelFor(value),
        };
      })
    );

    const readRows = computed(() =>
      unpinnedValues(props.modelValue, props.options).map((option) => ({
        value: option.value,
        label: option.label,
        status: readStatusFor(props.modelValue, option.value),
      }))
    );

    const setWrite = (status: TaskStatus, value: string) =>
      emit("update:modelValue", setWriteValue(props.modelValue, status, value));

    const toggleRead = (value: string, status: TaskStatus) =>
      emit(
        "update:modelValue",
        toggleReadStatus(props.modelValue, value, status)
      );

    return {
      mappedStatuses,
      writeRows,
      readRows,
      getColor,
      setWrite,
      toggleRead,
    };
  },
});
</script>

<style scoped lang="scss">
.status-value-mapping {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sentence-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.sentence-text {
  @extend %font-body-regular;

  @include themed() {
    color: t($grey-60);
  }
}

.write-options {
  display: flex;
  flex-direction: column;
  min-width: 160px;
  padding: 4px;
  gap: 2px;
}

.write-choice {
  cursor: pointer;
  border-radius: 4px;

  &:hover,
  &.active {
    @include themed() {
      background-color: t($bg-hover);
    }
  }
}

.grid {
  display: grid;
  align-items: center;
  gap: 8px 12px;

  &.read-grid {
    grid-template-columns: 1fr auto;
  }
}

.grid-header {
  @extend %font-body-medium;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding-bottom: 4px;

  &.right {
    justify-content: flex-end;
  }

  @include themed() {
    color: t($grey-60);
  }
}

.status-picks {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
}

.no-pointer {
  pointer-events: none;
}

.pick-chip {
  cursor: pointer;
  border-radius: $border-radius-m;
}

.pick-trigger {
  display: flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: $border-radius-m;
  cursor: pointer;

  @include themed() {
    border: 1px solid t($grey-30);
    color: t($grey-80);
  }

  &:hover {
    @include themed() {
      border-color: t($grey-50);
    }
  }
}

.pick-placeholder {
  @extend %font-caption-regular;

  @include themed() {
    color: t($grey-50);
  }
}
</style>
